import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Folder, Search } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { api, extractApiError } from '../lib/api'

/**
 * Duplicate Task → multiple Lists.
 *
 * Opens from the task-list Duplicate action. Shows the user-facing
 * Space → Project(Folder) → List hierarchy as a searchable, collapsible,
 * multi-select checkbox tree (root lists appear directly under their Space).
 * The user picks one or more destination Lists — across Projects/Spaces in the
 * same tenant — and confirms; the backend clones the task into each, best-effort,
 * and returns a per-destination result. The source List is shown disabled (never
 * a destination), and the original task is never modified.
 *
 * Backend: POST /lists/:sourceListId/tasks/:taskId/duplicate with
 * { destinationListIds }. A per-open Idempotency-Key makes a double-click / retry
 * safe (the global idempotency middleware replays the first response).
 */

const MAX_DESTINATIONS = 50
const newIdemKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `dup-${Date.now()}-${Math.random().toString(36).slice(2)}`

/** One destination List row (checkbox). The source list is disabled. */
function ListRow({ list, sourceListId, checked, onToggle }) {
  const isSource = String(list.id) === String(sourceListId)
  return (
    <label
      className={
        'flex items-center gap-2 rounded px-2 py-1 text-sm ' +
        (isSource
          ? 'cursor-not-allowed text-[color:var(--color-ink-muted)]'
          : 'cursor-pointer text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]')
      }
    >
      <input
        type="checkbox"
        disabled={isSource}
        checked={checked}
        onChange={() => onToggle(list.id)}
        className="h-4 w-4 shrink-0 accent-[color:var(--color-primary)]"
      />
      <span className="truncate">
        {list.name}
        {isSource ? ' (source list)' : ''}
      </span>
    </label>
  )
}

/** The Project(Folder) → List rows for one Space; fetched lazily when the Space
 *  is expanded. Lists are grouped by folderId; folderId=null sits at Space root. */
function SpaceLists({ spaceId, search, sourceListId, selected, onToggle }) {
  const foldersQuery = useQuery({
    queryKey: ['dup-folders', spaceId],
    queryFn: async () => (await api.get(`/spaces/${spaceId}/folders`)).data?.data ?? [],
  })
  const listsQuery = useQuery({
    queryKey: ['dup-lists', spaceId],
    queryFn: async () => (await api.get(`/spaces/${spaceId}/lists`)).data?.data ?? [],
  })

  const q = search.trim().toLowerCase()
  const matchName = (name) => !q || String(name ?? '').toLowerCase().includes(q)

  const { rootLists, folderBlocks, empty } = useMemo(() => {
    const folders = foldersQuery.data ?? []
    const lists = listsQuery.data ?? []
    const byFolder = new Map()
    for (const l of lists) {
      const k = l.folderId ?? null
      if (!byFolder.has(k)) byFolder.set(k, [])
      byFolder.get(k).push(l)
    }
    const root = (byFolder.get(null) ?? []).filter((l) => matchName(l.name))
    const blocks = folders
      .map((f) => ({
        folder: f,
        // A folder matched by name shows all its lists; otherwise only matching lists.
        lists: (byFolder.get(f.id) ?? []).filter(
          (l) => matchName(f.name) || matchName(l.name),
        ),
      }))
      .filter((b) => b.lists.length > 0)
    return {
      rootLists: root,
      folderBlocks: blocks,
      empty: root.length === 0 && blocks.length === 0,
    }
  }, [foldersQuery.data, listsQuery.data, q]) // eslint-disable-line react-hooks/exhaustive-deps

  if (foldersQuery.isLoading || listsQuery.isLoading) {
    return <p className="pl-7 py-1 text-sm italic text-[color:var(--color-ink-muted)]">Loading…</p>
  }
  if (foldersQuery.isError || listsQuery.isError) {
    return <p className="pl-7 py-1 text-sm text-[color:var(--color-danger)]">Could not load lists.</p>
  }
  if (empty) {
    return (
      <p className="pl-7 py-1 text-sm italic text-[color:var(--color-ink-muted)]">
        {q ? 'No matching lists.' : 'No lists in this space.'}
      </p>
    )
  }

  return (
    <div className="pl-5">
      {rootLists.map((l) => (
        <ListRow
          key={l.id}
          list={l}
          sourceListId={sourceListId}
          checked={selected.has(l.id)}
          onToggle={onToggle}
        />
      ))}
      {folderBlocks.map(({ folder, lists }) => (
        <div key={folder.id} className="mt-1">
          <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)]">
            <Folder size={13} />
            <span className="truncate">{folder.name}</span>
          </div>
          <div className="pl-5">
            {lists.map((l) => (
              <ListRow
                key={l.id}
                list={l}
                sourceListId={sourceListId}
                checked={selected.has(l.id)}
                onToggle={onToggle}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function DuplicateTaskModal({
  open,
  onClose,
  sourceTask,
  sourceListId,
  workspaceId,
  // The source task's department. Destinations are scoped to this department —
  // a task is duplicated into other lists within its OWN department, not across
  // departments (a Company Admin has company-wide access, so the raw spaces list
  // would otherwise show every department). `null` = the "no department" bucket.
  sourceDepartmentId = null,
  onDuplicated,
}) {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState(() => new Set())
  const [selected, setSelected] = useState(() => new Set())
  // A fresh idempotency key each time the modal opens for a task.
  const idemKey = useMemo(() => newIdemKey(), [open, sourceTask?.id])

  // Reset transient state whenever the modal opens for a (new) task.
  useEffect(() => {
    if (open) {
      setSearch('')
      setExpanded(new Set())
      setSelected(new Set())
    }
  }, [open, sourceTask?.id])

  const spacesQuery = useQuery({
    queryKey: ['dup-spaces', workspaceId],
    enabled: open && !!workspaceId,
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/spaces`)).data?.data ?? [],
  })
  const spaces = spacesQuery.data ?? []
  // Only spaces in the SAME department as the source task (both-null included).
  const scopedSpaces = spaces.filter(
    (s) => (s.departmentId ?? null) === (sourceDepartmentId ?? null),
  )
  const q = search.trim().toLowerCase()
  // When searching, treat every space as expanded so its lists mount + filter.
  const isExpanded = (id) => (q ? true : expanded.has(id))

  const toggleSpace = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleList = (id) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else if (next.size >= MAX_DESTINATIONS) {
        toast.error(`You can select at most ${MAX_DESTINATIONS} lists.`)
        return prev
      } else next.add(id)
      return next
    })

  const mutation = useMutation({
    mutationFn: async () => {
      const destinationListIds = [...selected]
      const res = await api.post(
        `/lists/${sourceListId}/tasks/${sourceTask.id}/duplicate`,
        { destinationListIds },
        { headers: { 'Idempotency-Key': idemKey } },
      )
      return res.data
    },
    onSuccess: (data) => {
      // Refresh every destination's task list (the source is untouched).
      for (const id of selected) qc.invalidateQueries({ queryKey: ['tasks', id] })
      qc.invalidateQueries({ queryKey: ['space-overview'] })
      const failed = Number(data?.failed ?? 0)
      if (failed > 0) toast.warning(data?.message ?? `Some lists failed (${failed}).`)
      else toast.success(data?.message ?? 'Task duplicated.')
      onDuplicated?.(data)
      onClose()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? () => {} : onClose}
      title="Duplicate Task"
      description={
        sourceTask
          ? `Copy “${sourceTask.name}” into other lists. The original task stays unchanged.`
          : undefined
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={selected.size === 0}
          >
            Duplicate Task{selected.size > 0 ? ` (${selected.size})` : ''}
          </Button>
        </>
      }
    >
      {/* Search */}
      <div className="relative mb-3">
        <Search
          size={15}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
        />
        <input
          type="text"
          maxLength={200}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects/lists..."
          className="w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] py-2 pl-9 pr-3 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-primary)]"
        />
      </div>

      {/* Tree */}
      <div className="max-h-[48vh] overflow-y-auto rounded-lg border border-[color:var(--color-rule)] p-1">
        {spacesQuery.isLoading ? (
          <p className="px-2 py-3 text-sm italic text-[color:var(--color-ink-muted)]">Loading spaces…</p>
        ) : spacesQuery.isError ? (
          <p className="px-2 py-3 text-sm text-[color:var(--color-danger)]">Could not load spaces.</p>
        ) : scopedSpaces.length === 0 ? (
          <p className="px-2 py-3 text-sm italic text-[color:var(--color-ink-muted)]">
            No other lists in this department.
          </p>
        ) : (
          scopedSpaces.map((space) => {
            const exp = isExpanded(space.id)
            return (
              <div key={space.id}>
                <button
                  type="button"
                  onClick={() => toggleSpace(space.id)}
                  className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm font-semibold text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)]"
                >
                  {exp ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  <span className="truncate">{space.name}</span>
                </button>
                {exp ? (
                  <SpaceLists
                    spaceId={space.id}
                    search={search}
                    sourceListId={sourceListId}
                    selected={selected}
                    onToggle={toggleList}
                  />
                ) : null}
              </div>
            )
          })
        )}
      </div>

      <p className="mt-3 text-sm font-medium text-[color:var(--color-ink)]">
        {selected.size} {selected.size === 1 ? 'List' : 'Lists'} selected
      </p>
    </Modal>
  )
}
